import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import ApprovalCell from './ApprovalCell';
import ApproverCommentCell from './ApproverCommentCell';
import {
    IDetailsColumnStyles,
    DetailsList,
    SelectionMode,
    Stack,
    IColumn,
    Dropdown,
    DetailsListLayoutMode,
    Persona,
    PersonaSize,
    Text,
    DatePicker,
    Icon,
    ScrollablePane,
    DayOfWeek,
    IDropdownOption,
    TooltipHost,
    Spinner,
} from '@fluentui/react';
import '../Styles/Approver.scss';
import { PeriodDay } from '../types/PeriodDay';
import { PageContent, ContentSection } from '../components/LayoutElements';
import { QuotesLoader } from '../components/QuotesLoader';
import ApproverCustomFiltersContainer from './ApproverCustomFiltersContainer';
import { ApproverFilterSection } from '../components/LayoutElements/ApproverFilterSection';
import { ApiCalls } from '../api/api';
import {
    ApproverCustomFiltersRequest,
    ApproverLinesCustomPeriod,
    Period,
    UserReportingPeriodComment,
    UserReportingPeriodState,
} from '../api/generated/data-contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useDebouncedEventHandler } from '@pum-spark/react-hooks';
import { APPROVER_BY_MANAGER, PERIODS_BY_DATE, USERS_BY_APPROVER } from '../constants/queryKeys';
import { useSetQueryData } from '../hooks/useSetQueryData';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';
import { useApproverListDetailsListGroups } from '../hooks/useApproverListDetailsListGroups';
import { useApproverTranslations } from '../hooks/useApproverTranslations';
import { format } from '../helpers/date';
import { useAllPeriods } from '../hooks/queries/useAllPeriods';
import { PeriodPicker } from './PeriodPicker';
import { differenceInDays, addDays, subDays } from 'date-fns';
import { getPeriodWithDate } from './getPeriodWithDate';
import { useLocalStorage } from '../hooks/useLocalStorageNew';
import { useMemoQueryKey } from '../hooks/useMemoQueryKey';

const NOT_CREATED = 'not created';

const headerStyle: Partial<IDetailsColumnStyles> = {
    cellTitle: { alignContent: 'center', justifyContent: 'center' },
};

const itemStyles: React.CSSProperties = {
    alignItems: 'end',
    display: 'flex',
    height: 60,
    width: 354,
    justifyContent: 'center',
};

type StateOption = Omit<IDropdownOption, 'key'> & { key: UserReportingPeriodState };
type Mode = 'single' | 'multi';

export default function Approver() {
    const { t: tAll } = useTranslation();
    const { t } = useTranslation('approval');
    const { stringsDatePicker, getTranslatedPeriod, urpStateOptions } = useApproverTranslations();
    const [selectedUrpStates, setSelectedUrpStates] = useState<any[]>(() => [...urpStateOptions]);
    const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
    const [containerWidth, setContainerWidth] = useState(null);

    const { data: allPeriods = null } = useAllPeriods({ suspense: true });
    const [mode, setMode] = useLocalStorage<Mode>('manager-approval-mode', 'multi');
    const [startDate, setStartDate] = useState(() => new Date());
    const [endDate, setEndDate] = useState(() => new Date());
    const nowPeriod = useMemo(() => getPeriodWithDate(allPeriods), [allPeriods]);

    const [selectedPeriod, setSelectedPeriod] = useState<Period | undefined | null>(nowPeriod);
    useEffect(() => {
        if (selectedPeriod) {
            setStartDate(new Date(selectedPeriod.startDate));
            setEndDate(new Date(selectedPeriod.endDate));
        }
    }, [selectedPeriod]);

    const { data: availableUsers } = useQuery({
        queryKey: [USERS_BY_APPROVER],
        queryFn: async () => {
            return ApiCalls.getUsersByApprover().then(res => {
                const users = res.data
                    .filter(user => user.isNotActive !== true)
                    .map(user => {
                        const approverid = user?.approver?.id ?? user.id;
                        const approverName = user?.approver?.name ?? user.name;
                        return { key: user.id, text: user.name, approverId: approverid, approverName: approverName };
                    })
                    .sort((a: any, b: any) => {
                        return a.text < b.text ? -1 : 1;
                    });
                return users;
            });
        },
    });

    const defaultFilteredPeriods = useRef([] as Period[]).current;

    const { data: filteredPeriods = defaultFilteredPeriods, isLoading } = useQuery({
        queryKey: [PERIODS_BY_DATE, availableUsers, startDate, endDate],
        queryFn: () =>
            ApiCalls.getPeriodsByDates({
                fromDate: startDate.getISOStringMidnight(),
                toDate: endDate.getISOStringMidnight(),
            }).then(res => res.data),
    });

    const {
        initialData: {
            configuration: { gridView, gridInputType, dateTimeFormat, approverCustomFiltersEnabled, shortDateFormat },
            aggregatedUser,
        },
    } = useConfigValuesState();

    useEffect(() => {
        if (availableUsers) {
            setSelectedUsers(availableUsers.filter(u => u.approverId === aggregatedUser.user.id));
        }
    }, [availableUsers, aggregatedUser.user.id]);

    const [customFilterChanged, setCustomFilterChanged] = useState<boolean>(false);

    const userFilterOptions = useMemo(() => {
        const allOptionObjForFilter = { key: 'selectalloption', text: t('filter.all'), approverId: null, approverName: null };
        return [allOptionObjForFilter, ...(availableUsers || [])];
    }, [availableUsers, t]);

    useEffect(() => {
        if (availableUsers && availableUsers.length === selectedUsers.length) {
            // Add select/deselect All option
            const allOptionObjForSelected = { key: 'selectalloption', text: t('filter.all') };
            setSelectedUsers([allOptionObjForSelected, ...selectedUsers]);
        }
    }, [availableUsers, selectedUsers, t]);

    const approverQkey = useMemoQueryKey(APPROVER_BY_MANAGER, startDate, endDate);

    const { data: approvalItems = null } = useQuery({
        queryKey: approverQkey,
        queryFn: () =>
            ApiCalls.getApproverLinesByManagerCustomPeriod({
                startDate: startDate.getISOStringMidnight(),
                endDate: endDate.getISOStringMidnight(),
            }).then(res => res.data),
        enabled: Boolean(availableUsers?.length && filteredPeriods?.length),
    });

    const setApprovalItems = useSetQueryData<typeof approvalItems>(approverQkey);

    const filteredApprovalItems = useMemo(() => {
        if (!approvalItems) {
            return [];
        }
        return approvalItems.filter(
            i =>
                selectedUsers.some(function (el) {
                    return el.key === i.userId;
                }) === true &&
                selectedUrpStates.some(function (el) {
                    return el.key === i.state;
                }) === true,
        );
    }, [approvalItems, selectedUrpStates, selectedUsers]);

    const approvalgroups = useApproverListDetailsListGroups(filteredApprovalItems);

    useEffect(() => {
        if (selectedUrpStates.length === 0) {
            return;
        }
        if (selectedUsers.length === 0) {
            return;
        }

        if (approvalgroups.length !== 0) {
            return;
        }
        setSelectedUrpStates(urpStateOptions);
    }, [approvalItems, filteredApprovalItems, selectedUrpStates.length, selectedUsers.length, approvalgroups, urpStateOptions]);

    const applyApproverCustomFilter = (appliedFilter?: ApproverCustomFiltersRequest) => {
        const allText = t('filter.all');
        if (!appliedFilter) {
            setSelectedUrpStates(urpStateOptions);
            const uFilterToApply = availableUsers.filter(u => u.approverId === aggregatedUser.user.id);
            if (uFilterToApply.length === userFilterOptions.length - 1) {
                setSelectedUsers([{ key: 'selectalloption', text: allText }, ...uFilterToApply]);
            } else {
                setSelectedUsers(uFilterToApply);
            }
            setCustomFilterChanged(false);
            return;
        }

        const sFilterToApply = urpStateOptions.filter(_ => appliedFilter.urpStateFilter.indexOf(_.key as string) > -1);
        if (sFilterToApply.length === urpStateOptions.length - 1) {
            setSelectedUrpStates([{ key: 'selectalloption', text: allText }, ...sFilterToApply]);
        } else {
            setSelectedUrpStates(sFilterToApply);
        }
        const uFilterToApply = userFilterOptions.filter(_ => appliedFilter.approverUsersFilter.indexOf(_.key) > -1);
        if (uFilterToApply.length === userFilterOptions.length - 1) {
            setSelectedUsers([{ key: 'selectalloption', text: allText }, ...uFilterToApply]);
        } else {
            setSelectedUsers(uFilterToApply);
        }
        setCustomFilterChanged(false);
    };

    const { mutate } = useMutation((dto: UserReportingPeriodComment) => ApiCalls.addComment(dto));
    const submitComment = useDebouncedEventHandler((dto: UserReportingPeriodComment) => mutate(dto));

    const userFilterDDChanged = (option: IDropdownOption, index?: number): void => {
        const allText = t('filter.all');
        if (option === undefined || option === null) {
            return;
        }

        if (option.key === 'selectalloption' && option.selected) {
            setSelectedUsers(
                userFilterOptions.map(opt => {
                    return { key: opt.key, text: opt.text };
                }),
            );
        } else if (option.key === 'selectalloption') {
            setSelectedUsers([]);
        } else if (option.selected === true) {
            if (selectedUsers.length === userFilterOptions.length - 2) {
                setSelectedUsers([{ key: 'selectalloption', text: allText }, ...selectedUsers, { key: option.key, text: option.text }]);
            } else {
                setSelectedUsers([...selectedUsers, { key: option.key, text: option.text }]);
            }
        } else {
            setSelectedUsers(selectedUsers.filter(opt => opt.key !== option.key && opt.key !== 'selectalloption'));
        }

        setCustomFilterChanged(true);
    };

    const urpStatesFilterDDChanged = (option: IDropdownOption, index?: number): void => {
        const allText = t('filter.all');
        if (option == null) {
            return;
        }

        if (option.key === 'selectalloption' && option.selected) {
            setSelectedUrpStates(
                urpStateOptions.map(opt => {
                    return { key: opt.key, text: opt.text };
                }),
            );
        } else if (option.key === 'selectalloption') {
            setSelectedUrpStates([]);
        } else if (option.selected === true) {
            if (selectedUrpStates.length === urpStateOptions.length - 2) {
                setSelectedUrpStates([{ key: 'selectalloption', text: allText }, ...selectedUrpStates, { key: option.key, text: option.text }]);
            } else {
                setSelectedUrpStates([...selectedUrpStates, { key: option.key, text: option.text }]);
            }
        } else {
            setSelectedUrpStates(selectedUrpStates.filter(opt => opt.key !== option.key && opt.key !== 'selectalloption'));
        }

        setCustomFilterChanged(true);
    };

    const [showHeaderAsDate, setShowHeaderAsDate] = useState(false);
    const toggleHeaderAsDate = useCallback(() => setShowHeaderAsDate(asDate => !asDate), []);

    const columns = useMemo((): IColumn[] => {
        if (!containerWidth) {
            return [];
        }

        const minWidthSmall = (80 * containerWidth) / 2000;
        const minWidthMedium = (100 * containerWidth) / 2000;
        const statewidth = minWidthMedium < 100 ? 100 : minWidthMedium;

        const minWidthLarge = (120 * containerWidth) / 2000;
        // (x + 20) = 20 is the padding the element get & the 40 in the end is the expander icon
        const columnsWidth = 8 * (minWidthSmall + 20) + (statewidth + 20) + 2 * (minWidthMedium + 20) + 3 * (minWidthLarge + 20) + 40;

        let personColumnsWidth = minWidthLarge;
        let nameColumnsWidth = minWidthLarge;

        if (containerWidth > columnsWidth) {
            nameColumnsWidth = minWidthLarge + (containerWidth - columnsWidth) * 0.5;
            personColumnsWidth = minWidthLarge + ((containerWidth - columnsWidth) * 0.2) / 2;
        }
        if (containerWidth < columnsWidth) {
            nameColumnsWidth = minWidthLarge + (containerWidth - columnsWidth) / 3;
            personColumnsWidth = minWidthLarge + (containerWidth - columnsWidth) / 3;
        }

        const columns: IColumn[] = [
            {
                key: 'personname',
                name: t('table.column.person'),
                fieldName: 'personName',
                isRowHeader: true,
                minWidth: nameColumnsWidth,
                isResizable: true,
                onRender: onRenderPersona,
            },
        ];
        if (gridView === 'period') {
            columns.push({
                key: 'period',
                name: gridInputType === 'percent' ? t('table.column.totalPerc') : t('table.column.total'),
                fieldName: 'period',
                minWidth: minWidthSmall,
                isResizable: true,
                styles: headerStyle,
                onRender: (item: ApproverLinesCustomPeriod, index, column) => {
                    const { hours, hasComments, periodDays } = getItemData(item, column);
                    return (
                        <ApprovalCell
                            disabled={item.state.toLowerCase() === NOT_CREATED}
                            value={hours}
                            userid={item.userId}
                            reportperiodid={item.reportPeriodId}
                            day={column.key}
                            hasUserComments={hasComments}
                            periodDays={periodDays}
                        />
                    );
                },
            });
        } else if (selectedPeriod && mode === 'single') {
            columns.push(
                //
                ...createColumnsFromStartAndEndDate({
                    start: selectedPeriod.startDate,
                    end: selectedPeriod.endDate,
                    translate: t,
                    containerWidth,
                    showHeaderAsDate,
                    toggleHeaderAsDate,
                    shortDateFormat,
                }),
                {
                    key: 'hours',
                    name: t('table.column.total'),
                    fieldName: 'hours',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: (item: ApproverLinesCustomPeriod, index, column) => {
                        const { fieldContent, hasComments } = getItemData(item, column);
                        return (
                            <ApprovalCell
                                disabled={item.state.toLowerCase() === NOT_CREATED}
                                value={fieldContent}
                                userid={item.userId}
                                reportperiodid={item.reportPeriodId}
                                day={column.key}
                                hasUserComments={hasComments}
                            />
                        );
                    },
                },
            );
        } else if (mode === 'multi') {
            columns.push(
                {
                    key: 'monday',
                    name: t('common:days.1'),
                    fieldName: 'monday',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: onRenderWeekdays,
                },
                {
                    key: 'tuesday',
                    name: t('common:days.2'),
                    fieldName: 'tuesday',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: onRenderWeekdays,
                },
                {
                    key: 'wednesday',
                    name: t('common:days.3'),
                    fieldName: 'wednesday',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: onRenderWeekdays,
                },
                {
                    key: 'thursday',
                    name: t('common:days.4'),
                    fieldName: 'fhursday',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: onRenderWeekdays,
                },
                {
                    key: 'friday',
                    name: t('common:days.5'),
                    fieldName: 'friday',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: onRenderWeekdays,
                },
                {
                    key: 'saturday',
                    name: t('common:days.6'),
                    fieldName: 'saturday',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: onRenderWeekdays,
                },
                {
                    key: 'sunday',
                    name: t('common:days.0'),
                    fieldName: 'sunday',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: onRenderWeekdays,
                },
                {
                    key: 'hours',
                    name: t('table.column.total'),
                    fieldName: 'hours',
                    minWidth: minWidthSmall,
                    isResizable: true,
                    styles: headerStyle,
                    onRender: (item: ApproverLinesCustomPeriod, index, column) => {
                        const { fieldContent, hasComments } = getItemData(item, column);
                        return (
                            <ApprovalCell
                                disabled={item.state.toLowerCase() === NOT_CREATED}
                                value={fieldContent}
                                userid={item.userId}
                                reportperiodid={item.reportPeriodId}
                                day={column.key}
                                hasUserComments={hasComments}
                            />
                        );
                    },
                },
            );
        }
        columns.push(
            {
                key: 'submitted',
                name: t('table.column.submitted'),
                fieldName: 'submitted',
                minWidth: minWidthMedium,
                isResizable: true,
                styles: headerStyle,
                onRender: (item: ApproverLinesCustomPeriod, index, column) => {
                    const { fieldContent } = getItemData(item, column);
                    return (
                        <Text styles={{ root: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', paddingRight: 6 } }}>
                            <span style={{ textAlign: 'center', display: 'block', fontSize: '0.8em', whiteSpace: 'normal' }}>
                                {fieldContent ? format(new Date(fieldContent), dateTimeFormat) : ''}
                            </span>
                        </Text>
                    );
                },
            },
            {
                key: 'submittedBy',
                name: t('table.column.submittedBy'),
                fieldName: 'submittedBy',
                minWidth: personColumnsWidth,
                isResizable: true,
                styles: headerStyle,
                onRender: onRenderPersona,
            },
            {
                key: 'submitComment',
                name: t('table.column.comment'),
                fieldName: 'submitComment',
                minWidth: minWidthMedium,
                isResizable: true,
                styles: headerStyle,
                onRender: (item: ApproverLinesCustomPeriod, index, column) => {
                    const { fieldContent } = getItemData(item, column);
                    return (
                        <ApproverCommentCell
                            disabled={item.state.toLowerCase() === NOT_CREATED}
                            commentDisabled={true}
                            labelText={'Comment from user'}
                            defaultValue={fieldContent}
                            reportPeriodId={item.reportPeriodId}
                            onUpdated={() => {}}
                        />
                    );
                },
            },
            {
                key: 'comment',
                name: t('table.column.reply'),
                fieldName: 'comment',
                minWidth: minWidthMedium,
                isResizable: true,
                styles: headerStyle,
                onRender: (item: ApproverLinesCustomPeriod, index, column) => {
                    const { fieldContent } = getItemData(item, column);
                    return (
                        <ApproverCommentCell
                            disabled={item.state.toLowerCase() === NOT_CREATED}
                            commentDisabled={false}
                            labelText={t('table.column.commentBox')}
                            defaultValue={fieldContent}
                            reportPeriodId={item.reportPeriodId}
                            onUpdated={newComment => {
                                submitComment({ comment: newComment, userReportingPeriodId: item.reportPeriodId });
                            }}
                        />
                    );
                },
            },
            {
                key: 'approverName',
                name: t('table.column.approver'),
                fieldName: 'approverName',
                minWidth: personColumnsWidth,
                isResizable: true,
                styles: headerStyle,
                onRender: onRenderPersona,
            },
            {
                key: 'state',
                name: t('table.column.state'),
                fieldName: 'state',
                minWidth: statewidth,
                isResizable: true,
                styles: headerStyle,
                onRender: (item: ApproverLinesCustomPeriod) => {
                    let dropdownClassName = '';

                    if ((item.state as UserReportingPeriodState) === 'Open') {
                        dropdownClassName = 'dropdowngraystyle';
                    } else if (item.state === 'Submitted') {
                        dropdownClassName = 'dropdownyellowstyle';
                    } else if ((item.state as UserReportingPeriodState) === 'Approved') {
                        dropdownClassName = 'dropdowngreenstyle';
                    } else if ((item.state as UserReportingPeriodState) === 'Rejected') {
                        dropdownClassName = 'dropdownredstyle';
                    } else if (item.autoapproval === true) {
                        dropdownClassName = 'dropdowngreenstyledisabled';
                    } else {
                        dropdownClassName = 'dropdowngreenstyle';
                    }
                    return (
                        <Stack>
                            <Stack.Item align="center">
                                <div>{item.autoapproval}</div>
                                <Dropdown
                                    disabled={item.autoapproval === true || item.state.toLowerCase() === NOT_CREATED || item.state.toLowerCase() === 'open'}
                                    placeholder={t('table.item.state.select')}
                                    defaultSelectedKey={item.state}
                                    onRenderCaretDown={(props, defaultRender) => {
                                        return item.autoapproval === true ? <></> : defaultRender(props);
                                    }}
                                    className={dropdownClassName}
                                    options={[
                                        { key: 'Open', text: t('table.item.state.open'), disabled: true } as StateOption,
                                        { key: 'Submitted', text: t('table.item.state.submitted'), disabled: true } as StateOption,
                                        { key: 'Rejected', text: t('table.item.state.rejected') } as StateOption,
                                        { key: 'Approved', text: t('table.item.state.approved') } as StateOption,
                                        { key: NOT_CREATED, text: t('table.item.state.notCreated'), disabled: true },
                                    ]}
                                    styles={{ dropdown: { minWidth: 115 } }}
                                    onChanged={option => {
                                        ApiCalls.setReportingPeriodState({
                                            userReportingPeriodId: item.reportPeriodId,
                                            state: option.key as UserReportingPeriodState,
                                            submitComment: item.submitComment,
                                        });
                                        setApprovalItems(items =>
                                            items.map(prevItem => {
                                                if (prevItem.reportPeriodId === item.reportPeriodId) {
                                                    return {
                                                        ...prevItem,
                                                        state: option.key as UserReportingPeriodState,
                                                    };
                                                }
                                                return prevItem;
                                            }),
                                        );
                                    }}
                                    id={item.reportPeriodId}
                                />
                            </Stack.Item>
                        </Stack>
                    );
                },
            },
        );

        return columns;
    }, [
        containerWidth,
        t,
        gridView,
        selectedPeriod,
        mode,
        gridInputType,
        showHeaderAsDate,
        toggleHeaderAsDate,
        shortDateFormat,
        dateTimeFormat,
        submitComment,
        setApprovalItems,
    ]);

    if (!availableUsers || !approvalgroups) {
        return <QuotesLoader loadingtext={t('loading.user')} />;
    }

    if (availableUsers.length === 0) {
        return (
            <div className="centeredtext">
                <h2>{t('disclaimer.manager')}</h2>
            </div>
        );
    }

    return (
        <>
            {approverCustomFiltersEnabled && (
                <ApproverFilterSection noPaddingBottom>
                    <ApproverCustomFiltersContainer
                        currentURPStateFilterArr={selectedUrpStates.map(_ => _.key)}
                        currentUserFilterArr={selectedUsers.map(_ => _.key)}
                        customFilterChanged={customFilterChanged}
                        filterSaved={() => setCustomFilterChanged(false)}
                        applyFilter={applyApproverCustomFilter}
                    />
                </ApproverFilterSection>
            )}
            <ContentSection>
                <Stack horizontal horizontalAlign="space-between" styles={{ root: { width: '100%' } }}>
                    <Stack>
                        <Dropdown
                            options={[
                                { key: 'single', text: t('approval:viewMode.single') },
                                { key: 'multi', text: t('approval:viewMode.multi') },
                            ]}
                            label={t('approval:viewMode.label')}
                            selectedKey={mode}
                            styles={{ dropdown: { width: 150 } }}
                            onChange={(e, option) => {
                                setMode(option.key as Mode);
                                const today = new Date();
                                if (option.key === 'single') {
                                    setStartDate(today);
                                } else {
                                    setStartDate(subDays(today, 28));
                                }
                                setEndDate(today);
                            }}
                        />
                    </Stack>
                    {approverCustomFiltersEnabled ? (
                        <TooltipHost content={<div>{t('custom.approve.tooltip')}</div>} closeDelay={250}>
                            <span style={itemStyles}>
                                {mode === 'multi' ? (
                                    <>
                                        <DatePicker
                                            allowTextInput
                                            firstDayOfWeek={DayOfWeek.Monday}
                                            ariaLabel="Select from date" //Might not be needed as it should default to a date
                                            placeholder={t('datepicker.placeholder.from')} //Might not be needed as it should default to a date
                                            value={startDate}
                                            onSelectDate={date => {
                                                setStartDate(date);
                                            }}
                                            styles={{ textField: { width: 150 }, root: { marginBottom: -5 } }}
                                            strings={stringsDatePicker}
                                            formatDate={getTranslatedPeriod}
                                            showWeekNumbers
                                        />

                                        <Icon
                                            iconName="ChromeMinimize"
                                            styles={{ root: { fontSize: '24px', height: '30px', width: '24px', margin: '0px 15px 0px 15px' } }}
                                        />

                                        <DatePicker
                                            allowTextInput
                                            firstDayOfWeek={DayOfWeek.Monday}
                                            ariaLabel="Select to date" //Might not be needed as it should default to a date
                                            placeholder={t('datepicker.placeholder.to')} //Might not be needed as it should default to a date
                                            value={endDate}
                                            onSelectDate={(date: Date | null | undefined) => {
                                                setEndDate(date);
                                            }}
                                            styles={{ textField: { width: 150 }, root: { marginBottom: -5 } }}
                                            strings={stringsDatePicker}
                                            formatDate={getTranslatedPeriod}
                                            showWeekNumbers
                                        />
                                    </>
                                ) : (
                                    <PeriodPicker allPeriods={allPeriods} setSelectedPeriod={setSelectedPeriod} selectedPeriod={selectedPeriod} />
                                )}
                            </span>
                        </TooltipHost>
                    ) : (
                        <span style={itemStyles}>
                            {mode === 'multi' ? (
                                <>
                                    <DatePicker
                                        allowTextInput
                                        firstDayOfWeek={DayOfWeek.Monday}
                                        ariaLabel="Select from date" //Might not be needed as it should default to a date
                                        placeholder={t('datepicker.placeholder.from')} //Might not be needed as it should default to a date
                                        value={new Date(startDate)}
                                        onSelectDate={date => {
                                            setStartDate(date);
                                        }}
                                        styles={{ textField: { width: 150 }, root: { marginBottom: -5 } }}
                                        strings={stringsDatePicker}
                                        formatDate={getTranslatedPeriod}
                                        showWeekNumbers
                                    />

                                    <Icon
                                        iconName="ChromeMinimize"
                                        styles={{ root: { fontSize: '24px', height: '30px', width: '24px', margin: '0px 15px 0px 15px' } }}
                                    />

                                    <DatePicker
                                        allowTextInput
                                        firstDayOfWeek={DayOfWeek.Monday}
                                        ariaLabel="Select to date" //Might not be needed as it should default to a date
                                        placeholder={t('datepicker.placeholder.to')} //Might not be needed as it should default to a date
                                        value={endDate}
                                        onSelectDate={(date: Date | null | undefined) => {
                                            setEndDate(date);
                                        }}
                                        styles={{ textField: { width: 150 }, root: { marginBottom: -5 } }}
                                        strings={stringsDatePicker}
                                        formatDate={getTranslatedPeriod}
                                        showWeekNumbers
                                    />
                                </>
                            ) : (
                                <PeriodPicker allPeriods={allPeriods} setSelectedPeriod={setSelectedPeriod} selectedPeriod={selectedPeriod} />
                            )}
                        </span>
                    )}
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                        <Dropdown
                            label={t('filter.userLabel')}
                            placeholder={t('filter.user')}
                            options={userFilterOptions}
                            selectedKeys={selectedUsers.map(u => {
                                return u.key;
                            })}
                            multiSelect
                            styles={{ dropdown: { width: 150 } }}
                            onChanged={userFilterDDChanged}
                            onRenderTitle={(defRenderProps, defRender) => {
                                return selectedUsers.length === userFilterOptions.length ? <span>{t('filter.all')}</span> : defRender(defRenderProps);
                            }}
                        />
                        <Dropdown
                            label={t('filter.stateLabel')}
                            placeholder={t('filter.state')}
                            options={urpStateOptions}
                            selectedKeys={selectedUrpStates.map(u => {
                                return u.key;
                            })}
                            multiSelect
                            styles={{ dropdown: { width: 150 } }}
                            onChanged={urpStatesFilterDDChanged}
                            onRenderTitle={(defRenderProps, defRender) => {
                                return selectedUrpStates.length === urpStateOptions.length ? (
                                    <span>{t('filter.option.state.all')}</span>
                                ) : (
                                    defRender(defRenderProps)
                                );
                            }}
                        />
                    </Stack>
                </Stack>
            </ContentSection>

            <PageContent>
                <ContentSection isDetailslist detailslistStickyHeader>
                    <ScrollablePane>
                        {isLoading ? (
                            <Spinner label={tAll('timesheet:loadingEntries')} />
                        ) : filteredPeriods.length ? (
                            <DetailsList
                                componentRef={(ref: any) => {
                                    // dirty trick to get the containers width
                                    if (ref && ref.props.viewport?.width && containerWidth !== ref.props.viewport?.width) {
                                        setContainerWidth(ref.props.viewport.width);
                                    }
                                }}
                                items={filteredApprovalItems}
                                groups={approvalgroups}
                                columns={columns}
                                selectionMode={SelectionMode.none}
                                layoutMode={DetailsListLayoutMode.fixedColumns}
                                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                                ariaLabelForSelectionColumn="Toggle selection"
                                checkButtonAriaLabel="Row checkbox"
                                groupProps={{
                                    showEmptyGroups: true,
                                }}
                                compact={true}
                                styles={{
                                    root: {
                                        // align submittedBy & approverName headers to the left
                                        [`div[data-item-key="submittedBy"] .ms-DetailsHeader-cellTitle, div[data-item-key="approverName"] .ms-DetailsHeader-cellTitle`]:
                                            {
                                                justifyContent: 'flex-start',
                                            },
                                    },
                                }}
                            />
                        ) : (
                            <div>No period found</div>
                        )}
                    </ScrollablePane>
                </ContentSection>
            </PageContent>
        </>
    );
}

type ColumnData = {
    date: Date;
    startDate: Date | string;
    endDate: Date | string;
};

const createColumnsFromStartAndEndDate = ({
    containerWidth,
    end,
    start,
    translate,
    showHeaderAsDate,
    toggleHeaderAsDate,
    shortDateFormat,
}: {
    start: string | Date;
    end: string | Date;
    containerWidth: number;
    translate: (key: string) => string;
    showHeaderAsDate: boolean;
    toggleHeaderAsDate: () => void;
    shortDateFormat: string;
}) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const span = differenceInDays(endDate, startDate) + 1;
    const minWidthSmall = (80 * containerWidth) / 2000;
    const columns: IColumn[] = [];
    let currentDay = startDate;
    for (let i = 0; i < span; i++) {
        const data: ColumnData = {
            date: currentDay,
            endDate,
            startDate,
        };
        columns.push({
            key: currentDay.getISOStringMidnight(),
            name: showHeaderAsDate ? format(data.date, shortDateFormat) : translate(`common:days.${currentDay.getDay()}`),
            minWidth: minWidthSmall,
            isResizable: true,
            styles: headerStyle,
            onRender: onRenderWeekdays,
            data,
            onColumnClick: toggleHeaderAsDate,
        });
        currentDay = addDays(currentDay, 1);
    }
    return columns;
};

const getPeriodDays = (startDate: Date | string, endDate: Date | string) => {
    const currentday: Date = new Date(startDate);
    endDate = new Date(endDate);
    const periodDaysArr: PeriodDay[] = [];
    let index: number = 0;
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    while (currentday <= endDate) {
        const dayName = days[currentday.getDay()];
        periodDaysArr.push({
            id: index,
            date: new Date(currentday),
            originalName: dayName,
            translatedName: '',
        });
        index++;
        currentday.setDate(currentday.getDate() + 1);
    }

    return periodDaysArr;
};

const getItemData = (item: ApproverLinesCustomPeriod, column: IColumn) => {
    let currentPeriodDay: PeriodDay;
    const fieldContent: string = item[column.fieldName as keyof ApproverLinesCustomPeriod] as string;
    let hasComments: boolean;
    let isHighlighted: boolean = false;
    let periodDays: PeriodDay[];
    let missingDay: boolean;
    const hours: string = String(item?.hours || 0);

    if (column.key === 'hours') {
        hasComments = false;
    } else if (column.data) {
        // Here we are in period mode
        const { date, startDate, endDate } = column.data as ColumnData;
        periodDays = getPeriodDays(startDate, endDate);
        currentPeriodDay = periodDays.find(periodDay => periodDay.date.getTime() === date.getTime());
        hasComments = item.dateTotalHours?.find(_ => new Date(_.date).getTime() === date.getTime())?.hasComment;
        missingDay = !periodDays.some(periodDay => periodDay.date.getTime() === date.getTime());
    } else if (['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(column.key)) {
        // Here we are in date span mode
        periodDays = getPeriodDays(new Date(item.startDate), new Date(item.endDate));
        currentPeriodDay = periodDays.find(_ => _.originalName.toLowerCase() === column.key);
        hasComments = item.dateTotalHours?.find(_ => new Date(_.date).getTime() === currentPeriodDay?.date?.getTime())?.hasComment;
        missingDay = !periodDays.some(_ => _.originalName.toLowerCase() === column.key);
    } else {
        hasComments = false;
        isHighlighted = true;
    }

    return {
        periodDays,
        currentPeriodDay,
        fieldContent,
        hasComments,
        isHighlighted,
        missingDay,
        hours,
    };
};

const onRenderWeekdays = (item?: ApproverLinesCustomPeriod, index?: number, column?: IColumn) => {
    const { currentPeriodDay, missingDay, hasComments } = getItemData(item, column);
    const value = (!missingDay && item.dateTotalHours?.find(_ => new Date(_.date).getTime() === currentPeriodDay.date?.getTime())?.hours.toString()) || '';
    return (
        <ApprovalCell
            date={currentPeriodDay?.date}
            disabled={missingDay || item.state.toLowerCase() === NOT_CREATED}
            value={value}
            userid={item.userId}
            reportperiodid={item.reportPeriodId}
            day={column.key}
            hasUserComments={hasComments}
        />
    );
};

const onRenderPersona = (item?: ApproverLinesCustomPeriod, index?: number, column?: IColumn) => {
    const { fieldContent } = getItemData(item, column);
    return fieldContent ? (
        <Persona text={fieldContent} size={PersonaSize.size24} styles={{ root: { height: '100%' }, primaryText: { fontWeight: 600, fontSize: 13 } }} />
    ) : null;
};
